import {
  CheckCircle,
  Info,
  WarningCircle,
  X,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

export type NotificationNoticeTone = 'info' | 'success' | 'error'

interface NotificationNoticeProps {
  tone: NotificationNoticeTone
  messageKey: string
  onRetry?: () => void
  retryLabelKey?: string
  onDismiss?: () => void
}

const TONE_CLASSES: Record<NotificationNoticeTone, string> = {
  info: 'rounded-md border border-line bg-mint text-teal',
  success: 'rounded-md border border-line bg-mint text-teal',
  error: 'rounded-md border border-line bg-[var(--danger-soft)] text-danger',
}

/**
 * Inline, catalog-driven feedback for load errors, read mutations and push
 * state. Errors are assertive alerts; informational and success messages are
 * polite live regions so screen readers hear the result of an action.
 */
export function NotificationNotice({
  tone,
  messageKey,
  onRetry,
  retryLabelKey = 'common.retry',
  onDismiss,
}: NotificationNoticeProps) {
  const { t } = useTranslation()
  const Icon = tone === 'error' ? WarningCircle : tone === 'success' ? CheckCircle : Info

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className={`flex flex-wrap items-center gap-3 px-4 py-3 text-sm font-semibold ${TONE_CLASSES[tone]}`}
    >
      <Icon size={18} weight="bold" aria-hidden="true" className="shrink-0" />
      <span className="min-w-0 flex-1">{t(messageKey)}</span>
      {onRetry ? (
        <button type="button" className="btn-ghost min-h-11 px-2" onClick={onRetry}>
          {t(retryLabelKey)}
        </button>
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          className="grid size-11 place-items-center rounded-full hover:bg-white/60"
          onClick={onDismiss}
          aria-label={t('common.close')}
        >
          <X size={17} weight="bold" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}
